import { assets, dialogs, extensions, view, cssGenerator } from '@yola/ws-sdk';
import getBackgroundImageOrigSource from 'src/js/modules/blocks/helpers/get-background-image-orig-source';
import { EDITOR_BREAKPOINTS } from 'src/js/modules/extensions/common/constants/breakpoints';
import matchesOrContains from './matches-or-contains';
import permissions from '../constants/permissions';
import showUnsupportedBrowserDialog from './show-unsupported-browser-dialog';
import showCopyToClipboardErrorDialog from './show-copy-to-clipboard-error-dialog.js';
import handleClipboardPermissionDenied from './handle-clipboard-permission-denied';
import handleClipboardPermissionDeniedReason from './handle-clipboard-permission-denied-reason.js';
import customDataTypes from '../constants/custom-data-types';

const { CONTENT_POSITION: CONTENT_POSITION_CLASS } = cssGenerator.constants.classNames;

const getContentPositionMap = (element) =>
  Object.fromEntries(
    EDITOR_BREAKPOINTS.map((bp) => [
      bp,
      view.accessors.getResponsiveClassValue({
        element,
        className: CONTENT_POSITION_CLASS,
        breakpoint: bp,
      })?.value ?? null,
    ])
  );

const getImageBlobFromURL = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return { [`web ${blob.type}`]: blob };
};

const getImagePropsBlobFromElement = async (element, imageElement, isBackgroundImage) => {
  let propsToCopy;
  if (isBackgroundImage) {
    propsToCopy = {
      position: element.getAttribute('background-position'),
      scale: element.getAttribute('background-scale'),
      overlay: element.hasAttribute('overlay'),
      overlayOpacity: element.getAttribute('overlay-opacity'),
    };
  } else {
    propsToCopy = {
      aspectRatio: element.aspectRatio,
      position: getContentPositionMap(element),
      scale: element.contentScale,
      alt: imageElement.getAttribute('alt'),
    };
  }
  if (!propsToCopy) return null;
  const blob = new Blob([JSON.stringify(propsToCopy)], { type: 'text/plain' });

  return { [customDataTypes.WEB_TEXT_PLAIN]: blob };
};

const copyImageToClipboard = async (element, config) => {
  const settings = extensions.accessors.getExtensionSettings(config.slug);
  const isBackgroundImage = element.matches(settings.blockWithBackgroundQuerySelector);
  const imageElement = matchesOrContains(element, settings.imageActionSelector);

  if (!isBackgroundImage && !imageElement) return;

  let permissionResponse = await handleClipboardPermissionDenied(
    permissions.features.CLIPBOARD_READ
  );

  if (permissionResponse.reason) {
    handleClipboardPermissionDeniedReason(permissionResponse.reason);
  }

  if (permissionResponse.status === permissions.statuses.DENIED) {
    return;
  }

  let imageSrc;
  if (isBackgroundImage) {
    imageSrc = getBackgroundImageOrigSource(element) || element.getAttribute('background-image');
  } else {
    imageSrc = imageElement.getAttribute('src');
  }

  if (!imageSrc) return;
  const imageBlob = await getImageBlobFromURL(assets.helpers.addBaseHref(imageSrc));
  const propsBlob = await getImagePropsBlobFromElement(element, imageElement, isBackgroundImage);
  let clipboardItem;

  try {
    clipboardItem = new ClipboardItem({
      ...imageBlob,
      ...propsBlob,
    });
  } catch (e) {
    console.error(e);
    showUnsupportedBrowserDialog();
    return;
  }

  await navigator.clipboard
    .write([clipboardItem])
    .then(dialogs.operations.hide)
    .catch(async (e) => {
      console.error(e);
      permissionResponse = await handleClipboardPermissionDenied(
        permissions.features.CLIPBOARD_READ
      );

      if (permissionResponse.reason) {
        handleClipboardPermissionDeniedReason(permissionResponse.reason);
      }

      if (permissionResponse.status === permissions.statuses.DENIED) {
        return;
      }

      if (e.message.includes('Document is not focused.')) {
        showCopyToClipboardErrorDialog();
        return;
      }

      showUnsupportedBrowserDialog();
    });
};

export default copyImageToClipboard;
